import { ExpandMore, Search } from "@mui/icons-material";
import {
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  Box,
  Stack,
  Typography,
  styled,
  Badge,
} from "@mui/material";
import { ReactNode, useCallback, useState } from "react";
import { DEFAULT_SEARCH_FIELDS } from "./studyFilter.consts";
import { Form, Formik } from "formik";
import { FormFields } from "components/form/FormField";
import { FocusedTooltip } from "components/tooltips/FocusedTooltip";
import { useDebouncedCallback } from "use-debounce";
import { useHomePageContext } from "pages/home-page/context/homepage.context";
import { GetStartedButton } from "components/buttons/Buttons";
import { useStudyFilterForm } from "./useStudyFilterForm";
import { CustomFab } from "components/buttons/CustomFab";
import { useStudyFilterCount } from "./useStudyFilterCount";
import { areObjectsDifferent } from "utils/validation/validation";
import { useUserType } from "hooks/useUserType";



export const StudyFilter = () => {
  const studyFilterCount = useStudyFilterCount();
  const { userTypeColour } = useUserType();
  return (
    <FocusedTooltip tooltipContent={<StudyFilterContent />}>
      <CustomFab ariaLabel="search-filter"
      sx={{
        background: userTypeColour,
        color: "white",
        "&:hover": {
          color: userTypeColour,
        },
      }}
      >
        <Badge
          badgeContent={studyFilterCount !== 0 ? studyFilterCount : null}
          color="info"
          sx={{ 
            span: { top: "-5px" }, 
          }}
        >
          <Search style={{ marginRight: "5px" }} />
          <Typography>Search</Typography>
        </Badge>
      </CustomFab>
    </FocusedTooltip>
  );
};

const StudyFilterContent = () => {
  const {
    initialValues,
    formFields,
    handleValueChange,
    clearFilters,
    resetToggleKey,
  } = useStudyFilterContent();

  const onValueUpdate = useDebouncedCallback((values) => {
    handleValueChange(values);
  }, 2000);

  const { userTypeColour } = useUserType();

  return (
    <Stack>
      <Formik
        key={`form-${resetToggleKey}`}
        initialValues={initialValues}
        validateOnBlur
        validate={(values) => {
          onValueUpdate(values);
        }}
        onSubmit={() => {}}
      >
        {({ values, resetForm }) => (
          <Form 
            key={`form `}
            //style={{ border: '2px solid blue' }}
          >
            {Object.keys(formFields).map((value: string, index) => {
              if (DEFAULT_SEARCH_FIELDS.includes(value)) {
                const fieldItem = formFields[value];
                const fieldValue = values[value] ?? null;
                return (
                  <FormFields
                    value={fieldValue as string | null}
                    key={`${value} ${index} ${resetToggleKey}`}
                    fieldName={value}
                    field={fieldItem}
                  />
                );
              }
              return [];
            })}
            <AdvancedFilters key={` ${resetToggleKey}`}>
              {Object.keys(formFields).flatMap((value: string, index) => {
                if (DEFAULT_SEARCH_FIELDS.includes(value)) {
                  return [];
                }
                const fieldItem = formFields[value];
                const fieldValue = values[value] ?? null;
                return (
                  <FormFields
                    value={fieldValue as string | null}
                    key={`${value} ${index} ${resetToggleKey}`}
                    fieldName={value}
                    field={fieldItem}
                  />
                );
              })}
            </AdvancedFilters>
            <Box
              style={{
                marginTop: "1rem",
                width: "100%",
              }}
            >
              <GetStartedButton
                onClick={() => {
                  clearFilters();
                  resetForm();
                }}
                style={{backgroundColor: userTypeColour, marginBottom: '5%' }}
              >
                Clear Filters
              </GetStartedButton>
            </Box>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

const useStudyFilterContent = () => {
  const {
    state: { filters },
  } = useHomePageContext();
  const formFields = useStudyFilterForm();
  const { dispatch } = useHomePageContext();

  // Reset toggle key is to force a rerender of the form when clearing search params
  const [resetToggleKey, setResetToggleKey] = useState<boolean>(false);

  const clearFilters = useCallback(() => {
    setResetToggleKey((prev) => !prev);
    dispatch({
      type: "CHANGE_SEARCH_PARAMS",
      payload: { filters: {} },
    });
  }, [setResetToggleKey, dispatch]);

  const handleValueChange = useCallback(
    (values) => {
      const updatedFilters = Object.entries(values).reduce(
        (acc, [key, value]) => {
          acc[key] = value;
          return acc;
        },
        {}
      );
      if (areObjectsDifferent(updatedFilters, filters)) {
        dispatch({
          type: "CHANGE_SEARCH_PARAMS",
          payload: { filters: updatedFilters },
        });
      }
    },
    [dispatch, filters]
  );

  return {
    initialValues: filters,
    formFields,
    handleValueChange,
    clearFilters,
    resetToggleKey,
  };
};

const Accordion = styled(MuiAccordion)(({ theme }) => ({
  boxShadow: "none",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: "auto",
  },
}));

const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: "1rem 0",
  minHeight: 26,
  "&.Mui-expanded": {
    minHeight: 26,
  },
  "& .MuiAccordionSummary-content": {
    margin: "0.1rem 0",
    "&.Mui-expanded": {
      margin: "0.1rem 0",
    },
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(1),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

interface AdvancedFilterProps {
  children: ReactNode;
}

const AdvancedFilters = ({ children }: AdvancedFilterProps) => {
  const { userTypeColour } = useUserType();
  return (
    <Accordion defaultExpanded style = {{backgroundColor: userTypeColour, color: '#FFFFFF', marginTop: '5%', borderRadius: '5px'}}>
      <AccordionSummary
        expandIcon={<FilterHeader />}
        aria-controls="panel1-content"
        id="panel1-header"
      >
        <Typography style = {{margin: '5%'}}>Advanced Filters</Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};

const FilterHeader = () => <ExpandMore />;
